/**
 * Shared-access state helpers.
 *
 * When a user steps into a shared account, we stash the owner's id, name,
 * permission level, and unwrapped private key (PEM) in sessionStorage. The
 * axios interceptor adds the X-Acting-On-Behalf-Of header on every request.
 *
 * Returning to your own account = clear the keys here.
 */
import { setPrivateKey } from './crypto';

const ACTING_KEY = 'acting_on_behalf_of';
const OWN_PRIVATE_KEY_BACKUP = 'own_private_key_pem';

/**
 * Switch into a shared account. Backs up the user's own private key (so we
 * can restore it on switch-back) and replaces it with the owner's.
 */
export function enterSharedAccount({ ownerId, ownerName, permission, invitationId, ownerPrivateKeyPem, ownPrivateKeyPem }) {
  // Backup the user's own private key so switching back is instant.
  if (ownPrivateKeyPem) {
    sessionStorage.setItem(OWN_PRIVATE_KEY_BACKUP, ownPrivateKeyPem);
  }
  sessionStorage.setItem(ACTING_KEY, JSON.stringify({
    owner_id: ownerId,
    owner_name: ownerName,
    permission,
    invitation_id: invitationId,
  }));
  // Swap the active private key in sessionStorage to the owner's so all
  // existing decrypt flows just work.
  setPrivateKey(ownerPrivateKeyPem);
}

export function leaveSharedAccount() {
  const own = sessionStorage.getItem(OWN_PRIVATE_KEY_BACKUP);
  if (own) setPrivateKey(own);
  sessionStorage.removeItem(OWN_PRIVATE_KEY_BACKUP);
  sessionStorage.removeItem(ACTING_KEY);
}

export function getActingOnBehalfOf() {
  try {
    const raw = sessionStorage.getItem(ACTING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
